import React from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { useLocation, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { useTheme } from '../contexts/ThemeContext';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import MrtInfoDialog from './MrtInfoDialog';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const { globalTheme, effectiveTheme, setGlobalTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [infoOpen, setInfoOpen] = React.useState(false);
  const [infoTone, setInfoTone] = React.useState('info');
  const [infoTitle, setInfoTitle] = React.useState('안내');
  const [infoMsg, setInfoMsg] = React.useState('');
  const [isFlutterApp, setIsFlutterApp] = React.useState(false);

  const showInfo = (tone, title, msg) => {
    setInfoTone(tone || 'info');
    setInfoTitle(title || '안내');
    setInfoMsg(String(msg || ''));
    setInfoOpen(true);
  };

  React.useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      GoogleAuth.initialize({
        clientId: '432734274639-p5q6774vvmichnvf1bomigk7l5armkqf.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
    }

    // Check if running inside MRT Flutter WebView
    const checkFlutter = () => {
      if (window.FlutterNativeAuth || navigator.userAgent.includes('MrtAppWebView')) {
        setIsFlutterApp(true);
        return true;
      }
      return false;
    };

    if (!checkFlutter()) {
      // Poll a few times in case injection is delayed
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (checkFlutter() || attempts > 10) {
          clearInterval(interval);
        }
      }, 100);
    }
  }, []);

  // 에러 객체에서 상세 정보를 추출하는 유틸리티 함수
  const extractError = (err) => {
    if (!err) return '알 수 없는 오류';
    if (typeof err === 'string') return err;
    if (err.message) return err.message;

    try {
      // JSON.stringify가 실패할 수 있으므로 안전하게 처리
      const str = JSON.stringify(err, Object.getOwnPropertyNames(err));
      // 빈 객체 문자열 '{}' 인 경우 처리
      if (str === '{}') {
        return `[객체 오류] ${String(err)}`;
      }
      return str;
    } catch (e) {
      return `[파싱 불가 오류] ${String(err)}`;
    }
  };

  const handleNativeLogin = async () => {
    try {
      if (window.FlutterNativeAuth) {
        // [Flutter] Flutter 브릿지를 통해 네이티브 구글 로그인을 호출
        window.onFlutterGoogleLoginSuccess = (idToken) => {
          if (idToken) {
            handleSuccess({ credential: idToken });
          } else {
            showInfo('danger', '로그인 오류', 'Google 로그인 후 토큰을 받지 못했습니다.');
          }
        };
        window.onFlutterGoogleLoginFailure = (errorMsg) => {
          showInfo('danger', '로그인 오류', `Flutter 처리 중 오류가 발생했습니다. 상세: ${errorMsg}`);
        };
        window.FlutterNativeAuth.postMessage('START_LOGIN');
        return;
      }

      // [Capacitor] 기존 Capacitor 네이티브 구글 로그인 로직
      const googleUser = await GoogleAuth.signIn();
      const idToken = googleUser?.authentication?.idToken;

      if (idToken) {
        handleSuccess({ credential: idToken });
      } else {
        showInfo('danger', '로그인 오류', 'Google 로그인 후 토큰을 받지 못했습니다.');
      }
    } catch (error) {
      const errorDetail = extractError(error);
      console.error('네이티브 구글 로그인 처리 중 오류:', errorDetail, error);
      showInfo('danger', '로그인 오류', `로그인 처리 중 오류가 발생했습니다. 상세: ${errorDetail}`);
    }
  };

  const handleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      console.log('Decoded token:', decoded);

      // 백엔드 API로 토큰 전송하여 권한 그룹 확인
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/api/auth/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: credentialResponse.credential,
          email: decoded.email,
        }),
      });

      // ✅ 중요: response.ok를 먼저 확인하지 않으면(특히 401/500),
      // {detail: "..."} 같은 에러 JSON을 "authorized=false"로 오해해서
      // 첫 로그인에 "접근 권한 없음"이 뜰 수 있음.
      let data = null;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      if (!response.ok) {
        const msg = String(data?.detail || data?.message || '').trim() || `서버 응답 오류(${response.status})`;
        console.error('로그인 실패(/api/auth/verify):', response.status, data);
        showInfo('danger', '로그인 오류', msg);
        return;
      }

      // ✅ 호환성: authorized가 없더라도 session_token이 있으면 성공으로 간주
      const authorized = (data && typeof data === 'object')
        ? (data.authorized === true || !!String(data?.session_token || data?.sessionToken || '').trim())
        : false;

      if (authorized) {
        // ✅ 승인 흐름:
        // - pending(값 있음): 팝업 없이 대기 화면만 표시(대시보드에서 처리)
        // - not_registered(값 없음): 가입승인요청 화면에서 처리(팝업 없이)
        // 권한이 있는 사용자
        const sessionToken = String(data?.session_token || data?.sessionToken || '').trim();
        const sessionExpMsRaw = data?.session_expires_at_ms ?? data?.sessionExpiresAtMs ?? null;
        const sessionExpMs = (sessionExpMsRaw != null && String(sessionExpMsRaw).trim() !== '' && !Number.isNaN(Number(sessionExpMsRaw)))
          ? Number(sessionExpMsRaw)
          : null;
        const userData = {
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture,
          // ✅ 구글 ID 토큰 대신 백엔드 세션 토큰 사용(슬라이딩 만료)
          token: sessionToken || credentialResponse.credential,
          token_type: sessionToken ? 'session' : 'google',
          session_expires_at_ms: sessionToken ? sessionExpMs : null,
          permissions: data.user?.permissions || [],
          // ✅ 레벨(=role) 정보를 함께 저장: 그룹관리 등 화면 노출 제어에 사용
          level: (data?.user?.level ?? data?.user?.role ?? data?.level ?? data?.role ?? null),
          workspace_perms: data?.user?.workspace_perms ?? null,
          approved: (data?.approved !== false),
          approval_status: String(data?.approval_status || (data?.approved === false ? 'pending' : 'approved')),
        };
        localStorage.setItem('user', JSON.stringify(userData));
        onLoginSuccess(userData);

        // ✅ 로그인 후 이동:
        // - 보호 라우트에서 튕겨온 경우: state.from으로 복귀
        // - 직접 /login 진입: 승인된 계정은 /schedule, 미승인은 대시보드(/)
        const from = String(location?.state?.from || '').trim();
        const email = String(userData?.email || '').trim().toLowerCase();
        const isSuper = email === 'mrt@mrt.co.kr';
        const approvalStatus = String(userData?.approval_status || (userData?.approved === false ? 'pending' : 'approved')).trim();
        const isApproved = isSuper || approvalStatus === 'approved';
        const target = from || (isApproved ? '/schedule' : '/');

        // SPA navigate가 간헐적으로 씹히는 케이스(원탭/iframe 타이밍) 대비: fallback까지 보장
        try {
          navigate(target, { replace: true });
        } catch (e) {
          window.location.replace(target);
          return;
        }
        window.setTimeout(() => {
          try {
            if (window.location.pathname === '/login') window.location.replace(target);
          } catch (e) { /* ignore */ }
        }, 200);
      } else {
        // ✅ 승인 흐름/미등록 계정은 "접근 권한 없음"으로 막지 말고 대시보드로 유도
        // (가입승인요청/대기 안내는 대시보드에서 처리)
        const approvalStatus = String(data?.approval_status || '').trim();
        if (approvalStatus === 'pending' || approvalStatus === 'not_registered') {
          const sessionToken = String(data?.session_token || data?.sessionToken || '').trim();
          const sessionExpMsRaw = data?.session_expires_at_ms ?? data?.sessionExpiresAtMs ?? null;
          const sessionExpMs = (sessionExpMsRaw != null && String(sessionExpMsRaw).trim() !== '' && !Number.isNaN(Number(sessionExpMsRaw)))
            ? Number(sessionExpMsRaw)
            : null;
          const userData = {
            email: decoded.email,
            name: decoded.name,
            picture: decoded.picture,
            token: sessionToken || credentialResponse.credential,
            token_type: sessionToken ? 'session' : 'google',
            session_expires_at_ms: sessionToken ? sessionExpMs : null,
            permissions: data?.user?.permissions || [],
            level: (data?.user?.level ?? data?.user?.role ?? data?.level ?? data?.role ?? null),
            workspace_perms: data?.user?.workspace_perms ?? null,
            approved: (approvalStatus === 'approved'),
            approval_status: approvalStatus,
          };
          localStorage.setItem('user', JSON.stringify(userData));
          onLoginSuccess(userData);
          navigate('/', { replace: true });
          return;
        }

        // 권한이 없는 사용자(서버가 명시적으로 authorized=false로 내려준 경우)
        // ※ 서버 오류/응답 파싱 실패는 위에서 "로그인 오류"로 처리해야 함
        showInfo('danger', '접근 권한 없음', String(data?.message || '').trim() || '접근 권한이 없습니다. 관리자에게 문의하세요.');
      }
    } catch (error) {
      console.error('로그인 처리 중 오류:', error);
      showInfo('danger', '로그인 오류', '로그인 처리 중 오류가 발생했습니다.');
    }
  };

  const handleError = () => {
    console.error('로그인 실패');
    showInfo('danger', '로그인 실패', '로그인에 실패했습니다. 다시 시도해주세요.');
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <div className="login-theme-toggle" aria-label="보기 모드">
            <button
              type="button"
              className={globalTheme === 'light' ? 'login-theme-btn active' : 'login-theme-btn'}
              onClick={() => setGlobalTheme('light')}
              title="라이트 모드"
            >
              라이트
            </button>
            <button
              type="button"
              className={globalTheme === 'dark' ? 'login-theme-btn active' : 'login-theme-btn'}
              onClick={() => setGlobalTheme('dark')}
              title="다크 모드"
            >
              다크
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <img
              src="/mrt-logo.png"
              alt="MRT"
              onError={(e) => {
                try {
                  // 로컬(단독) 실행 시 정적파일 경로가 달라질 수 있어, 앱 public 로고로 fallback
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = `${process.env.PUBLIC_URL || ''}/logo192.png`;
                } catch (err) { /* ignore */ }
              }}
              style={{ height: '44px', width: 'auto' }}
            />
            <h1 style={{ margin: 0 }}>MRT Workspace</h1>
          </div>
          {/* 문구 제거 요청 */}
        </div>

        <div className="login-content">
          {(Capacitor.isNativePlatform() || isFlutterApp || window.FlutterNativeAuth) ? (
            <button
              onClick={handleNativeLogin}
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '12px 16px',
                background: '#fff',
                color: '#3c4043',
                border: '1px solid #dadce0',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
                margin: '0 auto',
                fontFamily: 'Roboto, "Noto Sans KR", sans-serif'
              }}
            >
              <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" /><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" /><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" /><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" /><path fill="none" d="M0 0h48v48H0z" /></svg>
              구글 워크스페이스로그인
            </button>
          ) : (
            <GoogleLogin
              onSuccess={handleSuccess}
              onError={handleError}
              useOneTap
              theme={effectiveTheme === 'dark' ? 'filled_black' : 'outline'}
              size="large"
              text="signin_with"
              shape="rectangular"
            />
          )}

          <div className="login-info">
            <p>※ 등록된 권한 그룹의 계정만 로그인 가능합니다.</p>
            <p>접근 권한이 필요한 경우 관리자에게 문의하세요.</p>
          </div>
        </div>
      </div>
      <MrtInfoDialog
        open={infoOpen}
        ariaLabel={infoTitle}
        tone={infoTone}
        title={infoTitle}
        message={infoMsg}
        onClose={() => setInfoOpen(false)}
        zIndex={36000}
      />
    </div>
  );
};

export default Login;

